import { viewport } from './map_state/viewport'
import { state } from './map_state/state'
import { loader } from './map_state/loader'
import { cropTool } from './tools/crop_tool'
import { getImageDimensions, hexToRgba, keyMapping, worldToScreen } from './helpers'
import { historyTool } from './tools/history_tool'
import { resetTool } from './tools/reset_tool'
import { rotateTool } from './tools/rotate_tool'
import { pathcalculatorTool } from './tools/path_calculator_tool'
import { edgeDistanceTool } from './tools/edge_distance_tool'
import { pointDistanceTool } from './tools/point_distance_tool'
import { addSensorTool } from './tools/add_sensor_tool'
import { rssiForRadius } from './rssi'
import { linearFlowTool } from './tools/linear_flow_tool'
import { dragTool } from './tools/drag_tool'
import { swapTool } from './tools/swap_tool'

export class Renderer {
  static render ({ syncUi = true, showLoader = true } = {}) {
    if (showLoader) loader.setStatusMessage('processing', 'Rendering...')

    const [canvW, canvH] = viewport.getCanvasDimensions()
    viewport.ctx.clearRect(0, 0, canvW, canvH)
    state.labelsOccupied = []

    if (syncUi) {
      Renderer.toggleModeVisibility()
      Renderer.toggleKeyMappings()
      dragTool.syncUi()
    }

    Renderer.drawGrids()
    Renderer.drawMapImage()
    Renderer.drawCropOverlay()
    Renderer.drawSelectedPointCrosses()
    Renderer.drawMeasurePath()
    Renderer.drawDistanceToEdges()
    if (state.visiblePointDistances) Renderer.drawPointDistances()
    if (state.visibleLinearFlows) Renderer.drawLinearFlows()
    if (state.visibleSensors) Renderer.drawSensors()

    if (showLoader) loader.setStatusMessage('success', 'Rendered')
  }

  static drawGrids () {
    const [camX, camY] = viewport.getCamCoordinates()
    const [canvW, canvH] = viewport.getCanvasDimensions()

    const startX = camX - (camX % state.gridSpacing)
    const startY = camY - (camY % state.gridSpacing)

    viewport.ctx.save()
    viewport.ctx.setLineDash(state.gridDash)
    viewport.ctx.strokeStyle = state.darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
    viewport.ctx.lineWidth = state.gridLineWidth

    const start = worldToScreen(startX, startY)
    const step = state.gridSpacing * state.zoomValue

    for (let tempX = Math.round(start.x) + 0.5; tempX < canvW + step; tempX += step) {
      viewport.ctx.beginPath()
      viewport.ctx.moveTo(tempX, 0)
      viewport.ctx.lineTo(tempX, canvH)
      viewport.ctx.stroke()
    }

    for (let tempY = Math.round(start.y) + 0.5; tempY < canvH + step; tempY += step) {
      viewport.ctx.beginPath()
      viewport.ctx.moveTo(0, tempY)
      viewport.ctx.lineTo(canvW, tempY)
      viewport.ctx.stroke()
    }

    viewport.ctx.restore()
  }

  static drawMapImage () {
    if (!state.mapImage) return

    const screen = worldToScreen(state.mapWorld.x, state.mapWorld.y)

    const w = state.mapImage.naturalWidth * state.zoomValue
    const h = state.mapImage.naturalHeight * state.zoomValue

    const cx = screen.x + w / 2
    const cy = screen.y + h / 2

    viewport.ctx.save()
    viewport.ctx.translate(cx, cy)
    viewport.ctx.drawImage(state.mapImage, -w / 2, -h / 2, w, h)
    viewport.ctx.restore()
  }

  static drawCropOverlay () {
    if (!state.cropMode) return

    const { x, y, w, h } = state.cropRect
    const p1 = worldToScreen(x, y)
    const p2 = worldToScreen(x + w, y + h)

    const rx = p1.x
    const ry = p1.y
    const rw = p2.x - p1.x
    const rh = p2.y - p1.y

    const rect = viewport.canvas.getBoundingClientRect()
    const cw = rect.width
    const ch = rect.height

    viewport.ctx.save()

    viewport.ctx.fillStyle = 'rgba(0,0,0,0.35)'
    viewport.ctx.beginPath()
    viewport.ctx.rect(0, 0, cw, ch)
    viewport.ctx.rect(rx, ry, rw, rh)
    viewport.ctx.fill('evenodd')

    viewport.ctx.strokeStyle = 'rgba(255,255,255,0.9)'
    viewport.ctx.lineWidth = 2
    viewport.ctx.setLineDash([6, 4])
    viewport.ctx.strokeRect(rx, ry, rw, rh)

    const s = 8
    viewport.ctx.setLineDash([])
    viewport.ctx.fillStyle = 'white'
    const handles = [
      [rx, ry], [rx + rw, ry], [rx, ry + rh], [rx + rw, ry + rh]
    ]
    handles.forEach(([hx, hy]) => viewport.ctx.fillRect(hx - s / 2, hy - s / 2, s, s))

    viewport.ctx.restore()
  }

  static drawSelectedPointCrosses () {
    if (state.tracerPointsWorld.length === 0) return

    viewport.ctx.save()
    viewport.ctx.strokeStyle = 'red'
    viewport.ctx.lineWidth = 2

    for (const wp of state.tracerPointsWorld) {
      const sp = worldToScreen(wp.x, wp.y)
      viewport.ctx.beginPath()
      viewport.ctx.moveTo(sp.x - 7, sp.y - 7)
      viewport.ctx.lineTo(sp.x + 7, sp.y + 7)
      viewport.ctx.moveTo(sp.x + 7, sp.y - 7)
      viewport.ctx.lineTo(sp.x - 7, sp.y + 7)
      viewport.ctx.stroke()
    }

    viewport.ctx.restore()
  }

  static drawMeasurePath (target = Renderer.getRenderTarget()) {
    if (state.tracerPathWorld.length < 2) return

    const { ctx, worldToScreen } = target

    ctx.save()
    ctx.strokeStyle = 'red'
    ctx.lineWidth = 2
    ctx.setLineDash([])

    const p0 = worldToScreen(
      state.tracerPathWorld[0].x,
      state.tracerPathWorld[0].y
    )

    ctx.beginPath()
    ctx.moveTo(p0.x, p0.y)

    for (const wp of state.tracerPathWorld) {
      const sp = worldToScreen(wp.x, wp.y)
      ctx.lineTo(sp.x, sp.y)
    }

    ctx.stroke()
    ctx.restore()
  }

  static drawDistanceToEdges () {
    if (!state.edgeDistanceMode) return
    if (!state.boundaryInfo || !state.cursorWorld) return
    if (!state.boundaryInfo.inside) return

    const p = worldToScreen(state.cursorWorld.x, state.cursorWorld.y)
    const lp = worldToScreen(state.boundaryInfo.aabb.left, state.cursorWorld.y)
    const rp = worldToScreen(state.boundaryInfo.aabb.right, state.cursorWorld.y)
    const tp = worldToScreen(state.cursorWorld.x, state.boundaryInfo.aabb.top)
    const bp = worldToScreen(state.cursorWorld.x, state.boundaryInfo.aabb.bottom)

    viewport.ctx.save()
    viewport.ctx.strokeStyle = 'rgba(255,0,0,0.9)'
    viewport.ctx.fillStyle = 'rgba(255,0,0,0.9)'
    viewport.ctx.lineWidth = 1
    viewport.ctx.setLineDash([4, 4])

    viewport.ctx.beginPath()
    viewport.ctx.moveTo(lp.x, lp.y); viewport.ctx.lineTo(p.x, p.y)
    viewport.ctx.moveTo(p.x, p.y); viewport.ctx.lineTo(rp.x, rp.y)
    viewport.ctx.moveTo(tp.x, tp.y); viewport.ctx.lineTo(p.x, p.y)
    viewport.ctx.moveTo(p.x, p.y); viewport.ctx.lineTo(bp.x, bp.y)
    viewport.ctx.stroke()

    viewport.ctx.setLineDash([])
    viewport.ctx.font = '12px system-ui'

    const pad = 6
    viewport.ctx.fillText(`${state.boundaryInfo.left} ${state.lenUnit}`, (lp.x + p.x) / 2 + pad, (lp.y + p.y) / 2 - pad)
    viewport.ctx.fillText(`${state.boundaryInfo.right} ${state.lenUnit}`, (rp.x + p.x) / 2 + pad, (rp.y + p.y) / 2 - pad)
    viewport.ctx.fillText(`${state.boundaryInfo.top} ${state.lenUnit}`, (tp.x + p.x) / 2 + pad, (tp.y + p.y) / 2 - pad)
    viewport.ctx.fillText(`${state.boundaryInfo.bottom} ${state.lenUnit}`, (bp.x + p.x) / 2 + pad, (bp.y + p.y) / 2 - pad)

    viewport.ctx.restore()
  }

  static drawPointDistances (target = Renderer.getRenderTarget()) {
    if (!state.pointDistanceMode && !state.segments.length && !state.startPoint) return

    const { ctx, worldToScreen } = target

    const drawSegment = (a, b, isPreview) => {
      const pa = worldToScreen(a.x, a.y)
      const pb = worldToScreen(b.x, b.y)

      ctx.save()
      ctx.globalAlpha = isPreview ? 0.8 : 1
      ctx.lineWidth = 2
      ctx.setLineDash(isPreview ? [6, 4] : [])
      ctx.strokeStyle = 'rgba(255,0,0,0.9)'
      ctx.beginPath()
      ctx.moveTo(pa.x, pa.y)
      ctx.lineTo(pb.x, pb.y)
      ctx.stroke()
      ctx.restore()

      const dot = (p) => {
        ctx.save()
        ctx.fillStyle = 'rgba(255,0,0,0.95)'
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }

      dot(pa)
      dot(pb)

      if (!state.visibleLables) return

      const distPx = Math.hypot(b.x - a.x, b.y - a.y)
      const text = state.calibration && state.lenPath
        ? `${Math.round(distPx * state.lenPath * 10) / 10} ${state.lenUnit}`
        : `${Math.round(distPx * 10) / 10}px`
      const mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 }

      ctx.save()
      ctx.font = '12px system-ui'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.lineWidth = 4
      ctx.lineJoin = 'round'
      ctx.strokeStyle = 'rgba(255,255,255,0.9)'
      ctx.strokeText(text, mid.x, mid.y)
      ctx.fillStyle = 'rgba(0,0,0,0.8)'
      ctx.fillText(text, mid.x, mid.y)
      ctx.restore()
    }

    for (const seg of state.segments) {
      if (seg?.a && seg?.b) drawSegment(seg.a, seg.b, false)
    }

    if (!state.pointDistanceMode || !state.cursorWorld) return
    if (state.startPoint) drawSegment(state.startPoint, state.cursorWorld, true)
  }

  static drawSensors (target = Renderer.getRenderTarget(), boundsOverride = null) {
    if (!state.mapImage) return

    let bounds = boundsOverride

    if (!bounds) {
      const aabb = getImageDimensions()
      const tl = target.worldToScreen(aabb.left, aabb.top)
      const br = target.worldToScreen(aabb.right, aabb.bottom)

      bounds = {
        left: tl.x,
        top: tl.y,
        right: br.x,
        bottom: br.y
      }
    }

    for (const s of state.sensors) {
      if (s.visible) Renderer.drawSensor(s, false, target, bounds)
    }

    if (state.addSensorMode && state.previewSensor) {
      Renderer.drawSensor(state.previewSensor, true, target, bounds)
    }
  }

  static drawSensor (sensor, ghost, target = Renderer.getRenderTarget(), boundsOverride = null) {
    const { ctx, zoom, worldToScreen, showZoneLabels } = target

    const p = worldToScreen(sensor.position.x, sensor.position.y)
    const aabb = getImageDimensions()
    if (!aabb && !boundsOverride) return

    const bounds = boundsOverride || (() => {
      const tl = worldToScreen(aabb.left, aabb.top)
      const br = worldToScreen(aabb.right, aabb.bottom)
      return { left: tl.x, top: tl.y, right: br.x, bottom: br.y }
    })()

    const zones = sensor.getZonePx()
    const rA = (zones.a != null) ? zones.a * zoom : null
    const rB = (zones.b != null) ? zones.b * zoom : null
    const fade = sensor.edgeFadePx * zoom

    ctx.save()
    ctx.globalAlpha = ghost ? 0.55 : 0.9

    const colB = hexToRgba(sensor.zoneBColor, 0.4)
    const colA = hexToRgba(sensor.zoneAColor, 0.6)

    if (rB != null) Renderer.drawZoneRing(p.x, p.y, rB, colB, fade, target)
    if (rA != null) Renderer.drawZoneRing(p.x, p.y, rA, colA, fade, target)

    const isSelected = !ghost && state.selectedSensorId && String(state.selectedSensorId) === String(sensor.id)
    const dotColor = isSelected ? 'rgba(255,120,0,1)' : sensor.sensorColor
    Renderer.drawDot(p.x, p.y, dotColor, ghost, target)

    ctx.restore()

    if (ghost || !state.visibleLables) return

    if (sensor.name) {
      const placedName = Renderer.placeLabel({
        text: sensor.name,
        anchorX: p.x,
        anchorY: p.y - 10,
        prefer: 'up',
        bounds,
        fontPx: Math.round(Math.max(10, Math.min(14, 12 * zoom)))
      }, target)
      Renderer.drawPlacedLabel(sensor.name, placedName, target)
    }

    if (!showZoneLabels || !state.calibration) return
    const showRSSI = state.visibleRSSI ?? true

    if (sensor.enableZoneA && rA != null) {
      const m = Number(sensor.zoneARadius ?? 0).toFixed(1)
      const rssi = sensor.rssiA ?? rssiForRadius(m, sensor.zoneRadiusUnit)
      const text = `${m}${sensor.zoneRadiusUnit}` + (showRSSI ? `, ${rssi}` : '')

      const placed = Renderer.placeLabel({
        text,
        anchorX: p.x + rA,
        anchorY: p.y,
        prefer: 'right',
        bounds,
        fontPx: 12
      }, target)
      Renderer.drawPlacedLabel(text, placed, target)
    }

    if (sensor.enableZoneB && rB != null) {
      const m = Number(sensor.zoneBRadius ?? 0).toFixed(1)
      const rssi = sensor.rssiB ?? rssiForRadius(m, sensor.zoneRadiusUnit)
      const text = `${m}${sensor.zoneRadiusUnit}` + (showRSSI ? `, ${rssi}` : '')

      const placed = Renderer.placeLabel({
        text,
        anchorX: p.x + rB,
        anchorY: p.y,
        prefer: 'right',
        bounds,
        fontPx: 12
      }, target)
      Renderer.drawPlacedLabel(text, placed, target)
    }
  }

  static drawZoneRing (cx, cy, radiusPx, color, fadePx, target = Renderer.getRenderTarget()) {
    const { ctx } = target

    const r0 = Math.max(0, radiusPx - fadePx)
    const r1 = radiusPx

    const g = ctx.createRadialGradient(cx, cy, r0, cx, cy, r1)
    g.addColorStop(0, 'rgba(0,0,0,0)')
    g.addColorStop(1, color)

    ctx.save()
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(cx, cy, r1, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  static drawDot (cx, cy, color, ghost, target = Renderer.getRenderTarget()) {
    const { ctx } = target

    const innerRadius = 6
    const outerRadius = 8

    ctx.save()
    ctx.globalAlpha = ghost ? 0.7 : 1

    ctx.beginPath()
    ctx.arc(cx, cy, outerRadius, 0, Math.PI * 2)
    ctx.fillStyle = 'white'
    ctx.fill()
    ctx.strokeStyle = 'rgba(128,128,128,1)'
    ctx.lineWidth = 1.5
    ctx.stroke()

    ctx.beginPath()
    ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()

    ctx.restore()
  }

  static drawPlacedLabel (text, placed, target = Renderer.getRenderTarget()) {
    const { ctx } = target

    ctx.save()
    ctx.textAlign = placed.align
    ctx.textBaseline = 'middle'
    ctx.lineWidth = 4
    ctx.lineJoin = 'round'
    ctx.strokeStyle = 'rgba(255,255,255,0.9)'
    ctx.strokeText(text, placed.x, placed.y)
    ctx.fillStyle = 'rgba(0,0,0,0.85)'
    ctx.fillText(text, placed.x, placed.y)
    ctx.restore()
  }

  static toggleModeVisibility () {
    cropTool.toggleVisibility()
    historyTool.toggleVisibility()
    resetTool.toggleVisibility()
    rotateTool.toggleVisibility()
    pathcalculatorTool.toggleVisibility()
    edgeDistanceTool.toggleVisibility()
    pointDistanceTool.toggleVisibility()
    addSensorTool.toggleVisibility()
    linearFlowTool.toggleVisibility()
    swapTool.toggleVisibility()
  }

  static toggleKeyMappings () {
    viewport.keyMpappingsContainer.innerHTML = ''
    let keys = null
    if (state.cropMode) keys = Object.entries(cropTool.keys)
    if (state.pointDistanceMode) keys = Object.entries(pointDistanceTool.keys)
    if (state.addSensorMode) keys = Object.entries(addSensorTool.keys)
    if (state.linearFlowMode) keys = Object.entries(linearFlowTool.keys)
    if (state.dragMode) keys = Object.entries(dragTool.keys)
    if (!keys) return

    keys.forEach(([key, value]) => { viewport.keyMpappingsContainer.innerHTML += keyMapping(key, value) })
  }

  static placeLabel ({ text, anchorX, anchorY, prefer = 'right', bounds, fontPx = 12 }, target = Renderer.getRenderTarget()) {
    const { ctx, labelsOccupied } = target
    const pad = 6

    ctx.font = `${fontPx}px system-ui`
    ctx.textBaseline = 'middle'

    const candidates = [
      { side: 'right', x: anchorX + 10, y: anchorY, align: 'left' },
      { side: 'left', x: anchorX - 10, y: anchorY, align: 'right' },
      { side: 'up', x: anchorX, y: anchorY - 14, align: 'center' },
      { side: 'down', x: anchorX, y: anchorY + 14, align: 'center' },
      { side: 'ur', x: anchorX + 10, y: anchorY - 14, align: 'left' },
      { side: 'dr', x: anchorX + 10, y: anchorY + 14, align: 'left' },
      { side: 'ul', x: anchorX - 10, y: anchorY - 14, align: 'right' },
      { side: 'dl', x: anchorX - 10, y: anchorY + 14, align: 'right' }
    ]

    candidates.sort((a, b) => (a.side === prefer ? -1 : 0) - (b.side === prefer ? -1 : 0))

    const within = (r) =>
      r.left >= bounds.left + pad &&
      r.right <= bounds.right - pad &&
      r.top >= bounds.top + pad &&
      r.bottom <= bounds.bottom - pad

    for (const c of candidates) {
      const r = Renderer.labelRect(text, c.x, c.y, c.align, fontPx, target)
      if (!within(r)) continue
      if (labelsOccupied.some(o => Renderer.rectsOverlap(o, r))) continue
      labelsOccupied.push(r)
      return { ...c, rect: r }
    }

    const c0 = candidates[0]
    let x = c0.x
    let y = c0.y
    const align = c0.align

    const w = Math.ceil(ctx.measureText(text).width)
    const h = fontPx + 6

    const clampX = (value) => {
      if (align === 'left') return Math.max(bounds.left + pad, Math.min(value, bounds.right - pad - w))
      if (align === 'right') return Math.max(bounds.left + pad + w, Math.min(value, bounds.right - pad))
      return Math.max(bounds.left + pad + w / 2, Math.min(value, bounds.right - pad - w / 2))
    }

    const clampY = (value) =>
      Math.max(bounds.top + pad + h / 2, Math.min(value, bounds.bottom - pad - h / 2))

    x = clampX(x)
    y = clampY(y)

    const offsets = [0, -14, 14, -28, 28, -42, 42]
    for (const dy of offsets) {
      const yy = clampY(y + dy)
      const rect = Renderer.labelRect(text, x, yy, align, fontPx, target)
      if (!labelsOccupied.some(o => Renderer.rectsOverlap(o, rect))) {
        labelsOccupied.push(rect)
        return { ...c0, x, y: yy, align, rect }
      }
    }

    const rect = Renderer.labelRect(text, x, y, align, fontPx, target)
    labelsOccupied.push(rect)
    return { ...c0, x, y, align, rect }
  }

  static rectsOverlap (a, b) {
    return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom)
  }

  static labelRect (text, x, y, align, fontPx = 12, target = Renderer.getRenderTarget()) {
    const { ctx } = target
    const w = Math.ceil(ctx.measureText(text).width)
    const h = fontPx + 6

    let left
    let right
    if (align === 'left') { left = x; right = x + w }
    if (align === 'right') { left = x - w; right = x }
    if (align === 'center') { left = x - w / 2; right = x + w / 2 }

    const top = y - h / 2
    const bottom = y + h / 2
    return { left, top, right, bottom, w, h }
  }

  static drawLinearFlows (target = Renderer.getRenderTarget()) {
    if (!state.mapImage) return

    for (const sensor of state.sensors) {
      for (const endId of sensor.flowEndSensorIds) {
        const endSensor = state.sensors.find(s => String(s.id) === String(endId))
        if (!endSensor) continue
        Renderer.drawArrow(sensor.position, endSensor.position, false, target)
      }
    }

    if (!state.linearFlowMode || !state.flowStartSensorId || !state.flowPreviewWorld) return

    const startSensor = state.sensors.find(s => String(s.id) === String(state.flowStartSensorId))
    if (!startSensor) return

    Renderer.drawArrow(startSensor.position, state.flowPreviewWorld, true, target)
  }

  static drawArrow (fromWorld, toWorld, isPreview = false, target = Renderer.getRenderTarget()) {
    const { ctx, worldToScreen, zoom } = target

    const a = worldToScreen(fromWorld.x, fromWorld.y)
    const b = worldToScreen(toWorld.x, toWorld.y)

    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.hypot(dx, dy)
    if (len < 1) return

    const ux = dx / len
    const uy = dy / len

    const arrowHead = 12 * (zoom / state.zoomValue)
    const arrowWidth = 7 * (zoom / state.zoomValue)

    const dotRadius = 4 * (zoom / state.zoomValue)

    const tipX = b.x - ux * dotRadius
    const tipY = b.y - uy * dotRadius

    const baseX = tipX - ux * arrowHead
    const baseY = tipY - uy * arrowHead

    const leftX = baseX + (-uy) * arrowWidth
    const leftY = baseY + ux * arrowWidth

    const rightX = baseX - (-uy) * arrowWidth
    const rightY = baseY - ux * arrowWidth

    ctx.save()
    ctx.strokeStyle = 'rgba(255,80,80,0.95)'
    ctx.fillStyle = 'rgba(255,80,80,0.95)'
    ctx.lineWidth = 2
    ctx.setLineDash(isPreview ? [6, 4] : [])
    ctx.globalAlpha = isPreview ? 0.85 : 1

    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(tipX, tipY)
    ctx.stroke()

    ctx.setLineDash([])
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(leftX, leftY)
    ctx.lineTo(rightX, rightY)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }

  static getRenderTarget (options = {}) {
    const ctx = options.ctx || viewport.ctx
    const zoom = options.zoom ?? state.zoomValue
    const labelsOccupied = options.labelsOccupied || state.labelsOccupied
    const worldToScreenFn = options.worldToScreen || worldToScreen
    const showZoneLabels = options.showZoneLabels ?? true

    return {
      ctx,
      zoom,
      labelsOccupied,
      worldToScreen: worldToScreenFn,
      showZoneLabels
    }
  }
}
